import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import moment from 'moment'

import { ReimbursementTransactionsFilter, getReimbursementTransactions, getReimbursementTransactionsForCsv } from 'core'
import { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'

import useCurrentProduct from 'common/hooks/useCurrentProduct'

import { Button, Col, RangePickerV2, Row, Search, Skeleton, Table } from 'common/components'
import { now, parseZoneFormat } from 'common/dateFormatters'
import { downloadCSV } from 'common/downloadFile'
import { useQueryRequest } from 'common/hooks'

import { transactionTableColumns } from './TransactionTableColumns'

import './styles.less'

interface TransactionsProps {
  planPeriod: PlanPeriodDto
}

const Transactions: React.FC<TransactionsProps> = ({ planPeriod }) => {
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()

  const [transactions, isLoading, sendGetTransactions] = useQueryRequest(null)
  const [, isExportLoading, sendGetTransactionsForExport] = useQueryRequest(null)

  const [dateRangeValues, setDateRangeValues] = useState<{ fromDate: string; toDate: string }>({
    fromDate: null,
    toDate: null,
  })

  const [filter, setFilter] = useState<ReimbursementTransactionsFilter>({
    searchValue: '',
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'expenseNumber',
    orderDescending: true,
    fromDate: parseZoneFormat(dateRangeValues.fromDate),
    toDate: parseZoneFormat(dateRangeValues.toDate),
  })

  useEffect(() => {
    setFilter({
      ...filter,
      fromDate: parseZoneFormat(dateRangeValues.fromDate),
      toDate: parseZoneFormat(dateRangeValues.toDate),
    })

    setDateRangeValues({
      fromDate: dateRangeValues.fromDate,
      toDate: dateRangeValues.toDate,
    })
  }, [planPeriod.id])

  useEffect(() => {
    ;(async () => {
      console.log('pp ', planPeriod.id)
      await sendGetTransactions(() =>
        getReimbursementTransactions(planPeriod.id, {
          ...filter,
          fromDate: dateRangeValues.fromDate,
          toDate: dateRangeValues.toDate,
        }),
      )
    })()
  }, [filter, planPeriod.id])

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      fromDate: dateRangeValues.fromDate,
      toDate: dateRangeValues.toDate,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  const onSearch = (searchValue: string) => {
    setFilter({
      ...filter,
      searchValue: searchValue,
      pageNumber: 1,
    })
  }

  const handleDateChange = (values: any) => {
    const fromDateValue = values ? values[0] : null
    const toDateValue = values ? values[1] : null

    setDateRangeValues({
      fromDate: fromDateValue,
      toDate: toDateValue,
    })
    setFilter({
      ...filter,
      fromDate: fromDateValue,
      toDate: toDateValue,
      pageNumber: 1,
    })
  }

  const handleCsvDownload = async () => {
    const response = await sendGetTransactionsForExport(() =>
      getReimbursementTransactionsForCsv(planPeriod.id, {
        ...filter,
        fromDate: dateRangeValues.fromDate,
        toDate: dateRangeValues.toDate,
      }),
    )

    if (response) {
      downloadCSV(response, `Transactions_${moment().format('MM/DD/YYYY')}.csv`)
    }
  }

  return (
    <Skeleton loading={isLoading}>
      <Row align={'middle'} justify={'space-between'}>
        <Col span={18}>
          <Row align={'middle'} gutter={10}>
            <Col xs={24} sm={24} md={8} lg={8} xl={8} className="mb-8">
              <Search onSearch={onSearch} allowClear enterButton />
            </Col>

            <Col xs={24} sm={24} md={16} lg={16} xl={16} className="mb-8">
              <Row align="middle" gutter={6} justify={'center'}>
                <Col>
                  <label>Submission Date</label>
                </Col>

                <Col>
                  <RangePickerV2 allowClear className={'w-100'} format="MM/DD/YYYY" onChange={handleDateChange} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        <Col span={2} className="flex flex_justify_end">
          <Button
            type="primary"
            icon="far fa-download"
            iconPosition="left"
            htmlType="button"
            loading={isExportLoading}
            disabled={transactions?.items.length === 0}
            onClick={handleCsvDownload}
            className={'w-100'}
          >
            {translate('admin-dashboard.reimbursements.reimbursements-history.download-csv-label')}
          </Button>
        </Col>
      </Row>

      <Table
        className={'t-reimbursement-transactions'}
        dataSource={transactions?.items}
        onChange={onTableChange}
        loading={isLoading}
        scroll={{ x: 1000 }}
        columns={transactionTableColumns(translate, filter, product)}
        pagination={{
          total: transactions?.totalItemCount,
          pageSize: transactions?.pageSize,
          current: transactions?.pageNumber,
        }}
      />
    </Skeleton>
  )
}

export default Transactions
